import { useState } from "react";
import { Calculator, Share2, Menu, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import LanguageSwitcher from "./LanguageSwitcher";
import { useI18n } from "../i18n/I18nContext";

export default function Header() {
    const [open, setOpen] = useState(false);
    const { t } = useI18n();

    const links = [
        { href: "/#calculator", label: t("nav.calculator"), key: "calculator" },
        { href: "/#rates", label: t("nav.vatRates"), key: "vat-rates" },
        { href: "/#how", label: t("nav.howItWorks"), key: "how-it-works" },
        { href: "/#faq", label: t("nav.faq"), key: "faq" },
        { href: "/badge", label: "Badge", key: "badge" },
    ];

    const handleShare = async () => {
        const shareData = {
            title: t("share.title"),
            text: t("share.text"),
            url: typeof window !== "undefined" ? window.location.href : "",
        };
        try {
            if (navigator.share) {
                await navigator.share(shareData);
            } else {
                await navigator.clipboard.writeText(shareData.url);
                toast.success(t("share.copied"));
            }
        } catch (e) {
            // user cancelled — silent
        }
    };

    return (
        <header
            data-testid="site-header"
            className="sticky top-0 z-40 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 border-b border-slate-200"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <div className="h-16 flex items-center justify-between gap-3">
                    <a href="#top" data-testid="logo-link" className="flex items-center gap-2 group shrink-0">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#10B981] text-white font-display font-black text-lg shadow-sm group-hover:-translate-y-px transition-transform">
                            €
                        </span>
                        <span className="font-display font-black text-xl tracking-tight text-slate-900">
                            VATcalc<span className="text-[#10B981]">.eu</span>
                        </span>
                    </a>

                    <nav className="hidden md:flex items-center gap-1">
                        {links.map((l) => (
                            <a
                                key={l.key}
                                href={l.href}
                                data-testid={`nav-${l.key}`}
                                className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 rounded-md hover:bg-slate-100 transition-colors"
                            >
                                {l.label}
                            </a>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2">
                        <div className="hidden sm:block">
                            <LanguageSwitcher />
                        </div>
                        <Button
                            data-testid="share-tool-btn"
                            onClick={handleShare}
                            variant="outline"
                            size="sm"
                            className="hidden lg:inline-flex border-slate-200 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                        >
                            <Share2 className="h-4 w-4 mr-1.5" />
                            {t("nav.share")}
                        </Button>
                        <a
                            href="#calculator"
                            data-testid="header-cta"
                            className="hidden sm:inline-flex h-9 items-center gap-1.5 rounded-md bg-[#10B981] px-3.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-600 hover:-translate-y-px transition-all"
                        >
                            <Calculator className="h-4 w-4" />
                            {t("nav.openCalculator")}
                        </a>
                        <button
                            data-testid="mobile-menu-toggle"
                            className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-700"
                            onClick={() => setOpen((v) => !v)}
                            aria-label="Toggle menu"
                        >
                            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                        </button>
                    </div>
                </div>

                {open && (
                    <div className="md:hidden pb-4 border-t border-slate-100 pt-3 flex flex-col gap-1">
                        {links.map((l) => (
                            <a
                                key={l.key}
                                href={l.href}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-${l.key}`}
                                className="px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-md"
                            >
                                {l.label}
                            </a>
                        ))}
                        <div className="mt-2">
                            <LanguageSwitcher compact />
                        </div>
                        <Button
                            data-testid="mobile-share-btn"
                            onClick={handleShare}
                            variant="outline"
                            size="sm"
                            className="mt-2 w-full border-slate-200 text-slate-700"
                        >
                            <Share2 className="h-4 w-4 mr-1.5" />
                            {t("nav.share")}
                        </Button>
                    </div>
                )}
            </div>
        </header>
    );
}
